import { AsyncForm, AsyncFormContentParams } from "src/components/common/forms/AsyncForm.tsx";
import { HorizontalBox } from "src/components/common/box/HorizontalBox";
import {
	CustomerOrderLineFormApi,
	CustomerOrderLineFormApi_InitData,
	CustomerOrderLineFormApi_SalesPartDto,
} from "src/api/generated/erp/sales/customerOrder/api/customerOrderLineFormApi.ts";
import { ObjectAttributeValueWithFieldId } from "src/api/generated/erp/common/objectAttributes/model/objectAttributeValueWithFieldId.ts";
import { OpenCustomerOrderChildLinesButton } from "src/components/views/erp/sales/customerOrderLine/utils/OpenCustomerOrderChildLinesButton.tsx";
import { useErrorDialog } from "src/components/common/dialogs/errorDialog/ErrorDialogContext.tsx";
import { CustomerOrderLine } from "src/api/generated/erp/db/types/tables/customerOrderLine.ts";
import { FormCommonProps, FormSubmitResult } from "src/components/common/forms/types.ts";
import { DefaultValues } from "react-hook-form";
import { FormAsyncSelectField } from "src/components/common/forms/fields/FormAsyncSelectField.tsx";
import { requireRule } from "src/components/common/forms/validation.ts";
import { SalesPartView } from "src/api/generated/erp/db/types/tables/salesPartView.ts";
import i18n from "i18next";
import { FormTextField } from "src/components/common/forms/fields/FormTextField.tsx";
import { FormDateField } from "src/components/common/forms/fields/FormDateField.tsx";
import { FormCheckbox } from "src/components/common/forms/fields/FormCheckbox.tsx";
import { FormNumberField } from "src/components/common/forms/fields/FormNumberField.tsx";
import { FormSelectField } from "src/components/common/forms/fields/FormSelectField.tsx";
import {
	AcquisitionMethod,
	AcquisitionMethodValues,
	getAcquisitionMethodLabel,
} from "src/api/generated/erp/db/types/enums/acquisitionMethod.ts";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { showAsyncDialog } from "src/components/common/dialogs/asyncDialog.ts";
import { CustomerOrderLineBeforeCreateConfiguratorView } from "src/components/views/erp/configurator/configuratorForm/impl/CustomerOrderLineBeforeCreateConfiguratorView.tsx";
import { formatMoneyWithCurrency, round } from "src/utils/numberUtils.ts";
import { formatLabelWithCurrencyCode, getDefaultVatCode } from "src/components/views/erp/sales/salesUtils.ts";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { IsoDateString } from "src/types/dateTime.ts";
import { createUnsavedCustomerOrderLineConfiguratorType } from "src/components/views/erp/configurator/configuratorForm/utils/partConfiguratorTypeCreators.ts";
import { getConfiguratorFormDialogTitle } from "src/components/views/erp/configurator/configuratorForm/utils/configuratorFormUtils.tsx";
import { useConfirmDialog } from "src/components/common/dialogs/confirmDialog/ConfirmDialogContext.ts";
import { faArrowsRotate, faExclamationTriangle } from "@fortawesome/pro-regular-svg-icons";
import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Typography } from "@mui/material";
import {
	ObjectAttributesEmbeddedForm,
	ObjectAttributesEmbeddedFormValues,
} from "src/components/views/erp/objectAttributes/ObjectAttributesEmbeddedForm.tsx";
import {
	getObjectAttributesEmbeddedFormDefaultValues,
	getObjectAttributesFromEmbeddedFormValues,
} from "src/components/views/erp/objectAttributes/ObjectAttributesEmbeddedForm.utils.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";
import { StockPartConfigurationSelectionDataGrid } from "src/components/views/erp/sales/customerOrderLine/StockPartConfigurationSelectionDataGrid.tsx";

export interface CustomerOrderLineFormProps extends FormCommonProps<number> {
	customerOrderId: number;
	customerOrderLineId: number | undefined;
	parentLineId: number | undefined;
	copySourceCustomerOrderLine?: CustomerOrderLine;
}

interface FormValues
	extends Omit<CustomerOrderLine, "salesPartId">,
		ObjectAttributesEmbeddedFormValues {
	salesPart: CustomerOrderLineFormApi_SalesPartDto;
	updateAcquisitionObjectDate: boolean;
}

export const CustomerOrderLineForm = (props: CustomerOrderLineFormProps) => {
	const {
		customerOrderId,
		customerOrderLineId,
		parentLineId,
		copySourceCustomerOrderLine,
		onCompleted,
		onFormEdited,
	} = props;
	const { openDialog } = useGenericDialog();
	const [configuratorClientStorageKey, setConfiguratorClientStorageKey] = useState(() => crypto.randomUUID());
	const [hasIncompleteConfiguration, setHasIncompleteConfiguration] = useState(false);

	function resetConfigurationState() {
		setHasIncompleteConfiguration(false);
		setConfiguratorClientStorageKey(crypto.randomUUID());
	}

	return (
		<AsyncForm<CustomerOrderLineFormApi_InitData, FormValues, number>
			fetch={() =>
				CustomerOrderLineFormApi.getFormInitData({
					customerOrderId: customerOrderId,
					customerOrderLineId: customerOrderLineId,
					parentLineId: parentLineId,
					copySourceCustomerOrderLineId: copySourceCustomerOrderLine?.customerOrderLineId,
				})
			}
			getDefaultValues={getDefaultValues}
			onCompleted={onCompleted}
			onFormEdited={onFormEdited}
			columns={2}
			agentContext={{
				key: defineAgentUiContextKey("erp.sales.customerOrderLine.form"),
				data: {
					customerOrderId,
					customerOrderLineId: customerOrderLineId ?? null,
					parentCustomerOrderLineId: parentLineId ?? null,
					copySourceCustomerOrderLineId: copySourceCustomerOrderLine?.customerOrderLineId ?? null,
				},
			}}
			render={(params) => (
				<FormContent {...params} {...props} onResetIncompleteConfiguration={resetConfigurationState} />
			)}
			submit={submitForm}
			footerExtraComponents={({ watch }, triggerSubmit) => (
				<>
					{customerOrderLineId == null &&
						watch("salesPart")?.partIsConfigurable &&
						watch("acquisitionMethod") === "WAREHOUSE" && (
							<AsyncButton
								label={i18n.t("select_configuration_from_stock")}
								onClick={() => triggerSubmit(selectStockConfigurationAndInsert)}
							/>
						)}
					{customerOrderLineId == null && watch("salesPart")?.partIsConfigurable && (
						<AsyncButton label={i18n.t("configure")} onClick={() => triggerSubmit(configureAndInsert)} />
					)}
					{hasIncompleteConfiguration && (
						<Typography
							color={"warning.main"}
							sx={{ display: "flex", alignItems: "center", gap: 1, marginRight: 1 }}
						>
							<FontAwesomeIcon icon={faExclamationTriangle} />
							{i18n.t("configuration_incomplete")}
						</Typography>
					)}
				</>
			)}
		/>
	);

	function getDefaultValues({
		customerOrder,
		customerOrderLine,
		salesPart,
		parentLine,
		defaultLineNumber,
		attributeValues,
		attributeFields,
	}: CustomerOrderLineFormApi_InitData): DefaultValues<FormValues> {
		const attributeFormValues = getObjectAttributesEmbeddedFormDefaultValues(
			attributeValues,
			attributeFields,
		);
		if (customerOrderLine != null) {
			return {
				...customerOrderLine,
				salesPart: salesPart ?? undefined,
				unitPrice:
					customerOrder.inputPricesWithVat ? customerOrderLine.unitPriceWithVat : customerOrderLine.unitPrice,
				updateAcquisitionObjectDate: true,
				...attributeFormValues,
			};
		} else if (copySourceCustomerOrderLine != null) {
			return {
				...copySourceCustomerOrderLine,
				customerOrderLineId: undefined,
				salesPart: salesPart ?? undefined,
				updateAcquisitionObjectDate: true,
				lineNumber: defaultLineNumber,
				...attributeFormValues,
			};
		} else {
			return {
				customerOrderId: customerOrderId,
				parentLineId: parentLineId,
				lineNumber: defaultLineNumber,
				plannedDeliveryDate: customerOrder.plannedDeliveryDate,
				updateAcquisitionObjectDate: true,
				salesQuantityAsSalesUnits: 1,
				discountPercentage: 0,
				vatCodeId: customerOrder.billingPlanEnabled ? customerOrder.vatCodeId : parentLine?.vatCodeId,
				...attributeFormValues,
			};
		}
	}

	async function submitForm(values: FormValues): Promise<FormSubmitResult<number>> {
		const customerOrderLine = mapFormValuesToCustomerOrderLine(values);
		const attributes = getObjectAttributesFromEmbeddedFormValues(values);
		if (customerOrderLineId != null) {
			await CustomerOrderLineFormApi.update({
				customerOrderLine: customerOrderLine,
				updateAcquisitionObjectDates: values.updateAcquisitionObjectDate,
				attributes,
			});
			return customerOrderLineId;
		} else {
			const configureBeforeInsert =
				hasIncompleteConfiguration ||
				copySourceCustomerOrderLine?.partConfigurationId != null ||
				(await CustomerOrderLineFormApi.getShouldConfigureBeforeInsert({
					customerOrderId: customerOrderLine.customerOrderId,
					salesPartId: customerOrderLine.salesPartId,
				}));
			return await insert({
				customerOrderLine,
				configureBeforeInsert,
				attributes,
			});
		}
	}

	async function configureAndInsert(values: FormValues): Promise<FormSubmitResult<number>> {
		const customerOrderLine = mapFormValuesToCustomerOrderLine(values);
		const attributes = getObjectAttributesFromEmbeddedFormValues(values);
		return await insert({
			customerOrderLine: customerOrderLine,
			configureBeforeInsert: true,
			attributes,
		});
	}

	async function selectStockConfigurationAndInsert(values: FormValues): Promise<FormSubmitResult<number>> {
		const customerOrderLine = {
			...mapFormValuesToCustomerOrderLine(values),
			partConfigurationId: null,
		};
		const attributes = getObjectAttributesFromEmbeddedFormValues(values);
		const stockPartConfigurationId = await showAsyncDialog<number>(openDialog, ({ onCompleted }) => ({
			title: i18n.t("stock_configuration_selection"),
			size: "lg",
			agentContext: {
				key: defineAgentUiContextKey("erp.sales.stockPartConfigurationSelection.dialog"),
				pathSegments: [i18n.t("select_configuration_from_stock")],
				data: {
					customerOrderId,
					salesPartId: customerOrderLine.salesPartId,
					salesQuantityAsSalesUnits: customerOrderLine.salesQuantityAsSalesUnits,
				},
			},
			content: (
				<StockPartConfigurationSelectionDataGrid
					customerOrderId={customerOrderId}
					salesPartId={customerOrderLine.salesPartId}
					salesQuantityAsSalesUnits={customerOrderLine.salesQuantityAsSalesUnits}
					onCompleted={onCompleted}
				/>
			),
		}));
		if (stockPartConfigurationId === undefined) return "interrupted";

		return insert({
			customerOrderLine,
			configureBeforeInsert: false,
			attributes,
			stockPartConfigurationId,
		});
	}

	async function insert({
		customerOrderLine,
		configureBeforeInsert,
		attributes,
		stockPartConfigurationId,
	}: {
		customerOrderLine: CustomerOrderLine;
		configureBeforeInsert: boolean;
		attributes: ObjectAttributeValueWithFieldId[];
		stockPartConfigurationId?: number;
	}): Promise<FormSubmitResult<number>> {
		if (configureBeforeInsert) {
			setHasIncompleteConfiguration(true);
			const configuratorType = createUnsavedCustomerOrderLineConfiguratorType({
				customerOrderLine: customerOrderLine,
				initialValuesFromConfigurationId: copySourceCustomerOrderLine?.partConfigurationId ?? undefined,
				clientStorageKey: configuratorClientStorageKey,
			});
			const dialogTitle = await getConfiguratorFormDialogTitle({ configuratorType: configuratorType });
			const partConfigurationId = await showAsyncDialog<number>(
				openDialog,
				({ onCompleted, onDataDirtyStateChanged }) => ({
					title: dialogTitle,
					agentContext: {
						key: defineAgentUiContextKey("erp.sales.unsavedCustomerOrderLineConfigurator.dialog"),
						pathSegments: [i18n.t("configure")],
						data: {
							customerOrderId,
							customerOrderLineId: null,
							parentCustomerOrderLineId: parentLineId ?? null,
							copySourceCustomerOrderLineId: copySourceCustomerOrderLine?.customerOrderLineId ?? null,
						},
					},
					content: (
						<CustomerOrderLineBeforeCreateConfiguratorView
							configuratorType={configuratorType}
							onCompleted={onCompleted}
							onDataDirtyStateChanged={onDataDirtyStateChanged}
						/>
					),
				}),
			);
			if (partConfigurationId === undefined) {
				return "interrupted";
			}

			customerOrderLine.partConfigurationId = partConfigurationId;
		}
		return await CustomerOrderLineFormApi.insert({
			customerOrderLine: customerOrderLine,
			attributes,
			stockPartConfigurationId,
		});
	}

	function mapFormValuesToCustomerOrderLine(values: FormValues): CustomerOrderLine {
		return {
			...values,
			salesPartId: values.salesPart.salesPartId,
		};
	}
};

interface FormContentProps
	extends CustomerOrderLineFormProps, AsyncFormContentParams<CustomerOrderLineFormApi_InitData, FormValues> {
	onResetIncompleteConfiguration: () => void;
}

const FormContent = ({
	parentLineId,
	copySourceCustomerOrderLine,
	data: {
		customerOrder,
		customerOrderLine,
		acquisitionObjectState,
		vatCodeOptions,
		productionLineOptions,
		currencyCode,
		domesticCurrencyCode,
	},
	control,
	formState,
	watch,
	setValue,
	getValues,
	onResetIncompleteConfiguration,
}: FormContentProps) => {
	const { logErrorAndShowOnDialog } = useErrorDialog();
	const showConfirmDialog = useConfirmDialog();

	const isPackageChildLine = parentLineId != null;
	const isExistingCustomerOrderLine = customerOrderLine != null;

	const salesPart = watch("salesPart");
	const acquisitionMethod = watch("acquisitionMethod");
	const salesQuantityAsSalesUnits = watch("salesQuantityAsSalesUnits");

	const plannedDeliveryDateChanged = formState.dirtyFields.plannedDeliveryDate;
	const mayHaveAcquisitionObject =
		customerOrderLine != null &&
		["MANUFACTURE", "PURCHASE_DIRECT", "PURCHASE_TRANSIT"].includes(customerOrderLine.acquisitionMethod);

	const isUnreleasedSplitBomLine =
		salesPart?.acquisitionMethod === "SPLIT_BOM" && customerOrderLine?.releaseDate == null;
	// Cost editing is disabled for package lines but enabled for unreleased split BOM lines
	const costIsEditable = salesPart?.salesPartType !== "PACKAGE_" || isUnreleasedSplitBomLine;

	return (
		<>
			<FormAsyncSelectField
				control={control}
				name={"salesPart"}
				formValueType="option"
				autoFocus={!isExistingCustomerOrderLine && copySourceCustomerOrderLine == null}
				label={i18n.t("sales_part")}
				rules={requireRule()}
				fetchOptions={({ searchQuery, currentSelection }) =>
					CustomerOrderLineFormApi.getSalesPartOptions({
						customerOrderId: customerOrder.customerOrderId,
						searchQuery: searchQuery,
						currentSelection: currentSelection,
					})
				}
				getOptionKey={(option: CustomerOrderLineFormApi_SalesPartDto) => option.salesPartId}
				getOptionLabel={(option) => `${option.salesPartNo} | ${option.salesPartDescription}`}
				onChange={onSalesPartChanged}
				disabled={isExistingCustomerOrderLine || copySourceCustomerOrderLine != null}
				spanGridColumns
			/>
			<FormTextField control={control} name={"mark"} label={i18n.t("mark")} spanGridColumns />
			{getSalesQuantityField()}
			{!isPackageChildLine && (
				<>
					<FormDateField
						control={control}
						name={"plannedDeliveryDate"}
						label={i18n.t("planned_delivery_date")}
					/>
					{plannedDeliveryDateChanged && mayHaveAcquisitionObject && (
						<FormCheckbox
							control={control}
							name={"updateAcquisitionObjectDate"}
							label={i18n.t("customer_order_line_form_update_acquisition_object_date_field")}
							spanGridColumns
						/>
					)}
					{getUnitPriceField()}
					<FormCheckbox
						control={control}
						name={"priceLocked"}
						label={i18n.t("price_locked")}
						disabled={disabledIfInvoiced()}
					/>
					<FormNumberField
						control={control}
						name={"discountPercentage"}
						label={i18n.t("discount_percentage")}
						disabled={disabledIfInvoiced()}
						rules={requireRule()}
					/>
					<FormSelectField
						control={control}
						name={"vatCodeId"}
						label={i18n.t("vat_code")}
						options={vatCodeOptions}
						getOptionKey={(vatCode) => vatCode.vatCodeId}
						getOptionLabel={(vatCode) => vatCode.vatCodeName}
						rules={requireRule()}
						disabled={disabledIfInvoiced()}
						startNewGridRow
					/>
				</>
			)}
			{costIsEditable && (
				<>
					<FormNumberField
						control={control}
						name={"unitCost"}
						label={formatLabelWithCurrencyCode(i18n.t("cost"), domesticCurrencyCode)}
						startNewGridRow
					/>
					<FormCheckbox control={control} name={"costLocked"} label={i18n.t("cost_locked")} />
				</>
			)}
			<FormSelectField
				control={control}
				name={"acquisitionMethod"}
				label={i18n.t("acquisition_method")}
				options={getAvailableAcquisitionMethods()}
				getOptionKey={(method) => method}
				getOptionLabel={(method) => {
					if (method === "MANUFACTURE") return i18n.t("shop_order");
					return getAcquisitionMethodLabel(method);
				}}
				disabled={disabledIfReleased()}
				startNewGridRow
			/>
			{acquisitionMethod === "MANUFACTURE" && (
				<FormTextField
					control={control}
					name={"shopOrderBatchCode"}
					label={i18n.t("shop_order_batch")}
					disabled={disabledIfReleased()}
				/>
			)}
			{(acquisitionMethod === "MANUFACTURE" || isUnreleasedSplitBomLine) && (
				<>
					<FormNumberField
						control={control}
						name={"capacityQuantity"}
						label={i18n.t("capacity_quantity")}
						disabled={disabledIfReleased()}
					/>
					<FormSelectField
						control={control}
						name={"defaultProductionLineId"}
						label={i18n.t("default_production_line")}
						options={productionLineOptions}
						getOptionKey={(option) => option.productionLineId}
						getOptionLabel={(option) => option.productionLineName}
						disabled={disabledIfReleased()}
					/>
				</>
			)}
			<FormNumberField
				control={control}
				name={"lineNumber"}
				label={i18n.t("line_number")}
				type={"integer"}
				rules={requireRule()}
				startNewGridRow
			/>
			<ObjectAttributesEmbeddedForm control={control} />
			{customerOrderLine && <OpenCustomerOrderChildLinesButton customerOrderLine={customerOrderLine} />}
		</>
	);

	function getSalesQuantityField() {
		const labelSalesUnitPart = salesPart?.salesUnit != null ? ` (${salesPart.salesUnit})` : "";
		const label = `${i18n.t("quantity")}${labelSalesUnitPart}`;

		const disabled =
			acquisitionObjectState === "SHOP_ORDER_PLANNED" ? i18n.t("acquisition_shop_order_is_already_planned")
			: acquisitionObjectState === "PURCHASE_ORDER_RELEASED" ?
				i18n.t("acquisition_purchase_order_is_already_released")
			:	undefined;

		return (
			<FormNumberField
				control={control}
				name={"salesQuantityAsSalesUnits"}
				label={label}
				rules={requireRule()}
				onSubmit={(value) => {
					setValue("capacityQuantity", getDefaultCapacityQuantity(salesPart, value));
				}}
				disabled={disabled}
				helperText={
					salesPart?.availableQuantity != null ?
						i18n.t("available_quantity") + ": " + salesPart.availableQuantity
					:	undefined
				}
			/>
		);
	}

	function getUnitPriceField() {
		const labelVatPart = customerOrder.inputPricesWithVat ? i18n.t("with_vat") : i18n.t("without_vat");
		const labelSalesUnitPart = salesPart?.salesUnit != null ? ` (${salesPart.salesUnit})` : "";
		const label = `${formatLabelWithCurrencyCode(`${i18n.t("unit_price")}${labelSalesUnitPart}`, currencyCode)}, ${labelVatPart}`;
		const disabled =
			customerOrderLine?.customerOrderLineState === "INVOICED" ?
				i18n.t("customer_order_line_is_already_invoiced")
			:	undefined;
		return (
			<HorizontalBox sx={{ gap: 1 }}>
				<FormNumberField
					control={control}
					name={"unitPrice"}
					label={label}
					rules={requireRule()}
					startNewGridRow
					disabled={disabled}
					sx={{ flex: 1 }}
				/>
				<AsyncButton
					icon={faArrowsRotate}
					tooltip={i18n.t("update_price")}
					disabled={disabled}
					onClick={() => resetUnitPriceWithConfirmation()}
				/>
			</HorizontalBox>
		);
	}

	async function onSalesPartChanged(salesPart: SalesPartView | null) {
		try {
			onResetIncompleteConfiguration();
			const vatCodeId = getDefaultVatCodeId(salesPart);
			await resetUnitPriceSilently({ salesPart, vatCodeId });
			setValue("vatCodeId", vatCodeId);
			setValue("acquisitionMethod", salesPart?.acquisitionMethod ?? "NO_ACQUISITION");
			setValue("capacityQuantity", getDefaultCapacityQuantity(salesPart, salesQuantityAsSalesUnits));
			setValue(
				"unitCost",
				salesPart == null || salesPart.standardCost == null ?
					0
				:	round(salesPart.standardCost * salesPart.priceFactor, 2),
			);
		} catch (e) {
			logErrorAndShowOnDialog(e);
		}
	}

	function getDefaultVatCodeId(salesPart: SalesPartView | null): number {
		if (customerOrder.billingPlanEnabled) return customerOrder.vatCodeId;
		if (salesPart != null) return salesPart.vatCodeId;

		return getDefaultVatCode(vatCodeOptions).vatCodeId;
	}

	async function resetUnitPriceSilently({
		salesPart = getValues("salesPart"),
		vatCodeId = getValues("vatCodeId"),
		plannedDeliveryDate = getValues("plannedDeliveryDate"),
	}: {
		salesPart?: SalesPartView | null;
		vatCodeId?: number | null;
		plannedDeliveryDate?: IsoDateString | null;
	}) {
		if (getValues("priceLocked") === true) return;

		let defaultUnitPrice = 0;
		if (salesPart != null && vatCodeId != null) {
			defaultUnitPrice = await CustomerOrderLineFormApi.getSalesPartDefaultPrice({
				salesPartId: salesPart.salesPartId,
				customerOrderId: customerOrder.customerOrderId,
				withVat: customerOrder.inputPricesWithVat,
				vatCodeId: vatCodeId,
				plannedDeliveryDate: plannedDeliveryDate ?? customerOrder.plannedDeliveryDate,
			});
		}
		setValue("unitPrice", defaultUnitPrice);
	}

	async function resetUnitPriceWithConfirmation() {
		const newUnitPrice = await CustomerOrderLineFormApi.getSalesPartDefaultPrice({
			salesPartId: getValues("salesPart").salesPartId,
			customerOrderId: customerOrder.customerOrderId,
			withVat: customerOrder.inputPricesWithVat,
			vatCodeId: getValues("vatCodeId"),
			plannedDeliveryDate: getValues("plannedDeliveryDate"),
		});
		const confirmed = await showConfirmDialog({
			title: i18n.t("update_price"),
			message: i18n.t("update_customer_order_line_price_confirm_message", {
				currentPrice:
					getValues("unitPrice") == null ? "-" : formatMoneyWithCurrency(getValues("unitPrice"), currencyCode),
				newPrice: formatMoneyWithCurrency(newUnitPrice, currencyCode),
			}),
		});
		if (!confirmed) return;

		setValue("unitPrice", newUnitPrice);
	}

	function getDefaultCapacityQuantity(salesPart: SalesPartView | null, salesQuantityAsSalesUnits: number | null) {
		if (salesPart == null || salesPart.capacityQuantity == null || salesQuantityAsSalesUnits == null) return 0;

		return salesPart.capacityQuantity * salesPart.salesFactor * salesQuantityAsSalesUnits;
	}

	function disabledIfInvoiced() {
		return customerOrderLine?.customerOrderLineState === "INVOICED" ?
				i18n.t("customer_order_line_is_already_invoiced")
			:	undefined;
	}

	function disabledIfReleased() {
		return customerOrderLine?.releaseDate != null ? i18n.t("customer_order_line_is_already_released") : undefined;
	}

	function getAvailableAcquisitionMethods(): readonly AcquisitionMethod[] {
		if (salesPart == null) {
			return [...AcquisitionMethodValues];
		}
		switch (salesPart.salesPartType) {
			case "WAREHOUSE":
				return [...AcquisitionMethodValues];
			case "PACKAGE_":
				if (salesPart.partIsConfigurable) {
					return ["SPLIT_BOM", "NO_ACQUISITION"];
				} else {
					return ["NO_ACQUISITION"];
				}
			case "SERVICE":
				return ["NO_ACQUISITION"];
		}
	}
};
